// src/utils/tracking/fingerprint.ts
import type { INavigatorWithMemory } from '@/utils/tracking/types'

export const generateDeviceFingerprint = (): string => {
  const nav = navigator as INavigatorWithMemory

  const components = [
    nav.userAgent,
    nav.language,
    new Date().getTimezoneOffset(),
    nav.hardwareConcurrency,
    nav.deviceMemory,
    window.screen.colorDepth,
    window.screen.width + 'x' + window.screen.height,
    'canvas' + getCanvasFingerprint(),
    'audio' + getAudioFingerprint(),
    nav.webdriver,
    'plugins' + getPluginsFingerprint(),
  ].join('|')

  // Create a hash of the components
  return hashString(components)
}

// Helper functions
const getCanvasFingerprint = (): string => {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''

  // Draw something unique that will vary based on GPU/hardware
  canvas.width = 200
  canvas.height = 50
  ctx.textBaseline = 'top'
  ctx.font = '14px Arial'
  ctx.fillStyle = '#f60'
  ctx.fillRect(125, 1, 62, 20)
  ctx.fillStyle = '#069'
  ctx.fillText('Fingerprint', 2, 15)
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
  ctx.fillText('Fingerprint', 4, 17)

  return canvas.toDataURL()
}

const getAudioFingerprint = (): string => {
  try {
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)()
    const oscillator = audioContext.createOscillator()
    const analyser = audioContext.createAnalyser()
    const gain = audioContext.createGain()
    const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1)

    gain.gain.value = 0 // Mute the sound
    oscillator.type = 'triangle'
    oscillator.connect(analyser)
    analyser.connect(scriptProcessor)
    scriptProcessor.connect(gain)
    gain.connect(audioContext.destination)

    oscillator.start(0)

    const audioTimeConstant = audioContext.sampleRate.toString()
    audioContext.close()
    return audioTimeConstant
  } catch (e) {
    // Keep this error log - it's actionable for debugging
    console.error('Error getting audio fingerprint: ', e)
    return ''
  }
}

const getPluginsFingerprint = (): string => {
  const plugins = Array.from(navigator.plugins || [])
    .map(p => `${p.name}:${p.filename}`)
    .join(';')
  return plugins
}

const hashString = (str: string): string => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return hash.toString(36)
}
